import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    ArrowRight,
    Sparkles,
    BarChart3,
    Calculator,
    LineChart,
    Users,
    Zap,
    Star,
    Activity,
    PlayCircle,
    ShieldCheck,
    Globe2,
    TrendingUp,
    TrendingDown,
    Minus,
    BookOpen,
    Trophy,
    Quote,
    Building2,
    ExternalLink,
    Send,
} from "lucide-react";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import useSeo from "../../hooks/useSeo";
import Sparkline from "../../components/charts/Sparkline";

const TOOL_ICONS = {
    cost: Calculator,
    roi: LineChart,
    staff: Users,
    energy: Zap,
    starcheck: Star,
    occupancy: Activity,
};
const TOOL_HREFS = {
    cost: "/araclar/maliyet-hesaplayici",
    roi: "/araclar/roi-hesaplayici",
    staff: "/araclar/personel-planlayici",
    energy: "/araclar/enerji-hesaplayici",
    starcheck: "/yildiz-standartlari/kontrol",
    occupancy: "/araclar/doluluk-tahmincisi",
};
const CATEGORY_ICONS = {
    occupancy: TrendingUp,
    hotel_count: Building2,
    investment: BarChart3,
    tourism: Globe2,
};

export default function Home() {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || "tr").split("-")[0];
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/homepage");
                setData(data);
            } catch (_) {
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const tr = (obj, fallback = "") => {
        if (!obj) return fallback;
        if (typeof obj === "string") return obj;
        return obj[lang] || obj.tr || fallback;
    };

    useSeo({
        title: undefined,
        description: tr(data?.hero?.subtitle, t("home.hero_subtitle")),
        path: "/",
        ogImage:
            "https://customer-assets.emergentagent.com/job_otel-central/artifacts/tvucn0yf_wordmark-light.svg",
        structuredData: {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Otel Pro",
            url: (typeof window !== "undefined" && window.location.origin) || "",
        },
    });

    const hero = data?.hero || {};
    const liveData = data?.live_data || [];
    const toolCards = data?.tool_cards || [];
    const dataShowcase = data?.data_showcase || [];
    const blogList = data?.blog || [];
    const stories = data?.stories || [];
    const brands = data?.brands || [];

    const pulsePeriod = useMemo(() => {
        const d = new Date();
        const q = Math.floor(d.getMonth() / 3) + 1;
        return `Q${q} ${d.getFullYear()}`;
    }, []);

    return (
        <div data-testid="home-page">
            {/* SECTION 1: HERO */}
            <section className="relative overflow-hidden text-white" data-testid="home-hero">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <svg className="absolute inset-0 w-full h-full opacity-[0.06]" aria-hidden>
                    <defs>
                        <pattern id="op-grid" width="44" height="44" patternUnits="userSpaceOnUse">
                            <path d="M 44 0 L 0 0 0 44" fill="none" stroke="#39c8c1" strokeWidth="0.5" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#op-grid)" />
                </svg>
                <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-teal/15 blur-[120px]" />
                <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-gold/10 blur-[100px]" />
                <div className="relative max-w-7xl mx-auto px-6 pt-6 pb-6 lg:pt-8 lg:pb-8 grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-7 animate-fade-in-up">
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium tracking-widest uppercase bg-teal/15 text-teal border border-teal/30">
                            <Sparkles className="w-3.5 h-3.5" />
                            {tr(hero.eyebrow, t("tagline"))}
                        </span>
                        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
                            {tr(hero.title, t("home.hero_title"))}
                        </h1>
                        <p className="mt-6 text-lg sm:text-xl text-white/80 max-w-2xl leading-relaxed">
                            {tr(hero.subtitle, t("home.hero_subtitle"))}
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Link
                                to="/veri-merkezi"
                                data-testid="hero-cta-data"
                                className="inline-flex items-center gap-2 bg-teal hover:bg-teal-700 text-dark-navy font-semibold text-sm px-6 py-3.5 rounded-md shadow-lg hover:scale-[1.02]"
                            >
                                <BarChart3 className="w-4 h-4" /> Veri Merkezini Keşfet <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/araclar/maliyet-hesaplayici"
                                data-testid="hero-cta-cost"
                                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-700 text-dark-navy font-bold text-sm px-6 py-3.5 rounded-md shadow-lg"
                            >
                                <Calculator className="w-4 h-4" /> Maliyet Hesaplayıcı
                            </Link>
                        </div>
                    </div>

                    {/* SEKTÖR NABZI PANEL */}
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <div className="absolute -inset-6 bg-teal/15 rounded-2xl blur-2xl pointer-events-none" />
                            <div
                                className="relative bg-[#0a1421]/85 backdrop-blur-md border border-white/10 rounded-2xl p-7 shadow-2xl overflow-hidden"
                                data-testid="hero-live-panel"
                            >
                                <div className="flex items-center justify-between mb-5">
                                    <span
                                        data-testid="pulse-title"
                                        className="text-[11px] uppercase tracking-[0.22em] text-white/60 font-semibold"
                                    >
                                        Sektör Nabzı · {pulsePeriod}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-data-green font-bold">
                                        <span className="w-2 h-2 rounded-full bg-data-green animate-pulse shadow-[0_0_8px_2px_rgba(34,197,94,0.55)]" />
                                        Canlı
                                    </span>
                                </div>

                                <PulseBarChart bars={14} />

                                <div className="my-5 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                                <div className="space-y-0">
                                    {(liveData.length ? liveData : [1, 2, 3, 4]).slice(0, 4).map((d, i) =>
                                        liveData.length ? (
                                            <PulseRow key={d.id} item={d} idx={i} lang={lang} />
                                        ) : (
                                            <div key={i} className="h-14 bg-white/5 rounded my-2 animate-pulse" />
                                        ),
                                    )}
                                </div>

                                <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-[11px]">
                                    <span className="inline-flex items-center gap-1.5 text-white/55">
                                        <ShieldCheck className="w-3.5 h-3.5 text-data-green" />
                                        T.C. Kültür ve Turizm Bakanlığı verileri
                                    </span>
                                    <Link
                                        to="/veri-merkezi"
                                        data-testid="pulse-report-link"
                                        className="text-teal hover:text-white font-semibold inline-flex items-center gap-1"
                                    >
                                        Tüm raporu →
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 2: QUICK ACCESS TOOL CARDS */}
            <section className="bg-light-bg py-16" data-testid="home-quick-tools">
                <div className="max-w-7xl mx-auto px-6">
                    <SectionHeader
                        eyebrow="Hızlı Erişim"
                        title="Karar Destek Araçları"
                        subtitle="Saniyeler içinde profesyonel hesaplama, PDF rapor."
                        link={{ href: "/araclar", label: "Tüm Araçlar" }}
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
                        {(toolCards.length ? toolCards : []).slice(0, 4).map((tcard, i) => {
                            const Icon = TOOL_ICONS[tcard.tool] || Calculator;
                            const href = TOOL_HREFS[tcard.tool] || "/araclar";
                            return (
                                <Link
                                    key={i}
                                    to={href}
                                    data-testid={`quick-tool-${tcard.tool}`}
                                    className="lift-card group bg-white rounded-lg shadow-md hover:shadow-xl border border-slate-200 hover:border-teal/40 p-6 flex flex-col"
                                >
                                    <span className="inline-flex w-12 h-12 rounded-md items-center justify-center bg-teal/10 text-teal-700 group-hover:bg-teal group-hover:text-white transition-colors">
                                        <Icon className="w-5 h-5" />
                                    </span>
                                    <h3 className="mt-4 text-base font-bold text-dark-navy">{tr(tcard.title)}</h3>
                                    <p className="mt-2 text-xs text-deep-blue/80 flex-1">{tr(tcard.description)}</p>
                                    <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-teal-700">
                                        Aç <ArrowRight className="w-3 h-3" />
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SECTION 3: SECTOR DATA SHOWCASE */}
            <section className="bg-white py-16" data-testid="home-data-showcase">
                <div className="max-w-7xl mx-auto px-6">
                    <SectionHeader
                        eyebrow="Sektör Verileri"
                        title="Sektör Nabzı"
                        subtitle="Bakanlık, TÜRSAB ve sektör araştırmalarının özeti."
                        link={{ href: "/veri-merkezi", label: "Tüm Verileri Gör" }}
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
                        {dataShowcase.slice(0, 4).map((d) => (
                            <DataShowcaseCard key={d.id} item={d} lang={lang} />
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION 4: LATEST BLOG */}
            <section className="bg-light-bg py-16" data-testid="home-blog">
                <div className="max-w-7xl mx-auto px-6">
                    <SectionHeader
                        eyebrow="Yayın"
                        title="Sektör Haberleri"
                        subtitle="Sektörden derlenen analizler, trend ve yatırım rehberleri."
                        link={{ href: "/blog", label: "Tüm Yazıları Gör" }}
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
                        {blogList.slice(0, 4).map((p) => (
                            <Link
                                key={p.id}
                                to={`/blog/${p.slug}`}
                                data-testid={`home-blog-card-${p.slug}`}
                                className="lift-card group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl border border-slate-100"
                            >
                                <div className="aspect-[16/9] bg-light-bg relative overflow-hidden">
                                    {p.featured_image ? (
                                        <img src={p.featured_image} alt={tr(p.title)} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-deep-blue to-dark-navy flex items-center justify-center text-white/30">
                                            <BookOpen className="w-10 h-10" />
                                        </div>
                                    )}
                                </div>
                                <div className="p-5">
                                    <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal/10 text-teal-700 font-semibold">
                                        Sektör Analizi
                                    </span>
                                    <h3 className="mt-2 text-base font-bold text-dark-navy line-clamp-2">{tr(p.title)}</h3>
                                    <div className="mt-3 flex items-center justify-between text-xs text-deep-blue/60">
                                        <span className="font-data">{p.published_at ? new Date(p.published_at).toLocaleDateString("tr-TR") : ""}</span>
                                        <span>{p.reading_time || 4} dk</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                        {blogList.length === 0 && (
                            <div className="col-span-4 text-sm text-deep-blue/60 text-center py-6">
                                Yayınlanmış yazı bulunamadı.
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* SECTION 5: SUCCESS STORIES */}
            {stories.length > 0 && (
                <section className="bg-white py-16" data-testid="home-stories">
                    <div className="max-w-7xl mx-auto px-6">
                        <SectionHeader
                            eyebrow="Başarı Hikayeleri"
                            title="Sektör Liderlerinden"
                            subtitle="Otel sahipleri ve genel müdürlerin sektör deneyimleri."
                            link={{ href: "/basari-hikayeleri", label: "Tümünü Gör" }}
                        />
                        <div className="grid md:grid-cols-3 gap-5 mt-10">
                            {stories.slice(0, 3).map((s) => (
                                <div key={s.id} className="lift-card bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl border border-slate-100">
                                    <div className="aspect-video bg-dark-navy relative overflow-hidden flex items-center justify-center">
                                        {s.video_url || s.person?.photo ? (
                                            <img src={s.person?.photo || ""} alt={s.person?.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <Trophy className="w-12 h-12 text-gold/40" />
                                        )}
                                        {s.video_url && (
                                            <span className="absolute inset-0 flex items-center justify-center bg-dark-navy/40">
                                                <PlayCircle className="w-12 h-12 text-white" />
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-5">
                                        <Quote className="w-5 h-5 text-teal" />
                                        <p className="mt-2 text-sm text-deep-blue/85 line-clamp-3">{tr(s.featured_quote)}</p>
                                        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                                            <div>
                                                <div className="font-semibold text-dark-navy">{s.person?.name}</div>
                                                <div className="text-deep-blue/60">{s.hotel?.name}</div>
                                            </div>
                                            <span className="font-data text-gold-700">{"★".repeat(s.hotel?.star_level || 0)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* SECTION 6: ALL TOOLS GRID */}
            <section className="bg-light-bg py-16" data-testid="home-tools-grid">
                <div className="max-w-7xl mx-auto px-6">
                    <SectionHeader
                        eyebrow="İnteraktif Araçlar"
                        title="Profesyonel Karar Destek Setiniz"
                        subtitle="6 farklı hesaplayıcı + bakanlık standartları kontrol — tek panel."
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
                        {[
                            { tool: "cost", title: "Maliyet Hesaplayıcı", desc: "Otel açmak ne kadar tutar?" },
                            { tool: "roi", title: "ROI Hesaplayıcı", desc: "Yatırımınız ne zaman döner?" },
                            { tool: "staff", title: "Personel Planlayıcı", desc: "Kaç personel gerekli?" },
                            { tool: "energy", title: "Enerji Hesaplayıcı", desc: "Aylık enerji faturanız?" },
                            { tool: "starcheck", title: "Yıldız Kontrol", desc: "Kaç yıldızı hak ediyorsunuz?" },
                            { tool: "occupancy", title: "Doluluk Tahmincisi", desc: "Tahmini doluluk oranınız?" },
                        ].map((tcard) => {
                            const Icon = TOOL_ICONS[tcard.tool];
                            const href = TOOL_HREFS[tcard.tool];
                            return (
                                <Link
                                    key={tcard.tool}
                                    to={href}
                                    data-testid={`home-tool-${tcard.tool}`}
                                    className="lift-card group bg-deep-blue text-white rounded-lg p-6 shadow-md hover:shadow-xl hover:bg-dark-navy"
                                >
                                    <span className="inline-flex w-12 h-12 rounded-md items-center justify-center bg-teal/15 text-teal group-hover:bg-teal group-hover:text-dark-navy transition-colors">
                                        <Icon className="w-5 h-5" />
                                    </span>
                                    <h3 className="mt-4 text-lg font-bold">{tcard.title}</h3>
                                    <p className="mt-2 text-sm text-white/70">{tcard.desc}</p>
                                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-teal group-hover:text-gold">
                                        Bu aracı kullanın <ArrowRight className="w-4 h-4" />
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SECTION 7: STAR STANDARDS */}
            <section className="bg-white py-16" data-testid="home-stars">
                <div className="max-w-7xl mx-auto px-6">
                    <SectionHeader
                        eyebrow="Yıldız Standartları"
                        title="Bakanlık Standartları Veritabanı"
                        subtitle="1-5 yıldız tesisleri için zorunlu kriterler — kategori bazında detaylı veritabanı."
                    />
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-10">
                        {[1, 2, 3, 4, 5].map((s) => (
                            <Link
                                key={s}
                                to={`/yildiz-standartlari/${s}`}
                                data-testid={`home-star-${s}`}
                                className="lift-card text-center bg-light-bg hover:bg-gold/10 border-2 border-transparent hover:border-gold/40 rounded-lg p-5"
                            >
                                <div className="font-data text-3xl font-extrabold text-gold tracking-tight">
                                    {Array.from({ length: s }).map(() => "★").join("")}
                                </div>
                                <div className="mt-2 text-sm font-bold text-dark-navy">{s} Yıldız</div>
                                <div className="mt-1 text-[11px] text-deep-blue/70 uppercase tracking-wider">~{15 + s * 5} kriter</div>
                                <div className="mt-3 text-xs text-teal-700 font-semibold inline-flex items-center gap-1">
                                    İncele <ArrowRight className="w-3 h-3" />
                                </div>
                            </Link>
                        ))}
                    </div>
                    <div className="mt-8 text-center">
                        <Link
                            to="/yildiz-standartlari"
                            className="inline-flex items-center gap-2 bg-teal hover:bg-teal-700 text-dark-navy font-bold text-sm px-6 py-3 rounded-md shadow-md"
                            data-testid="home-stars-cta"
                        >
                            <Star className="w-4 h-4" />
                            İnteraktif Veritabanını Keşfet <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* SECTION 8: BRANDS */}
            <section className="bg-light-bg py-16" data-testid="home-brands">
                <div className="max-w-7xl mx-auto px-6">
                    <SectionHeader
                        eyebrow="Markalarımız"
                        title="Otel Pro Ekosistemi"
                        subtitle="3 farklı pencere, tek bir sektör vizyonu — her bir alan için özelleşmiş kaynak."
                    />
                    <div className="grid md:grid-cols-3 gap-5 mt-10">
                        {brands.slice(0, 3).map((b, i) => (
                            <a
                                key={i}
                                href={b.url}
                                target="_blank"
                                rel="nofollow noopener noreferrer"
                                data-testid={`home-brand-${i}`}
                                className="lift-card group bg-white rounded-lg shadow-md hover:shadow-xl border border-slate-200 hover:border-teal/40 p-6 flex flex-col"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="inline-flex w-12 h-12 rounded-md items-center justify-center bg-deep-blue/10 text-deep-blue group-hover:bg-deep-blue group-hover:text-white transition-colors">
                                        <Building2 className="w-5 h-5" />
                                    </span>
                                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-teal" />
                                </div>
                                <h3 className="mt-4 text-base font-bold text-dark-navy">{b.name}</h3>
                                <p className="mt-2 text-sm text-deep-blue/80 flex-1">{tr(b.description)}</p>
                                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-teal-700">
                                    Ziyaret Et <ArrowRight className="w-4 h-4" />
                                </span>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION 9: NEWSLETTER */}
            <NewsletterCta data={data} />
        </div>
    );
}

function NewsletterCta({ data }) {
    const { i18n, t } = useTranslation();
    const lang = (i18n.language || "tr").split("-")[0];
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const tr = (obj, fallback = "") => (obj && (obj[lang] || obj.tr)) || fallback;

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setLoading(true);
        try {
            await api.post("/newsletter/subscribe", { email: email.trim(), source: "homepage", lang });
            toast.success(t("newsletter.title") + " ✓");
            setEmail("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="newsletter"
            data-testid="home-newsletter"
            className="bg-dark-navy text-white relative overflow-hidden"
        >
            <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 30% 0%, rgba(57,200,193,0.4), transparent 50%), radial-gradient(circle at 100% 100%, rgba(214,176,107,0.3), transparent 50%)" }} />
            <div className="relative max-w-5xl mx-auto px-6 py-16 lg:py-24 text-center">
                <span className="text-xs uppercase tracking-[0.25em] text-teal font-semibold">
                    {tr(data?.newsletter?.title?.tr ? data.newsletter : null) ? "" : ""}
                </span>
                <h2 className="text-3xl sm:text-5xl font-bold tracking-tight">
                    {tr(data?.newsletter?.title, t("newsletter.title"))}
                </h2>
                <p className="mt-4 text-white/75 max-w-xl mx-auto">{tr(data?.newsletter?.subtitle, t("newsletter.subtitle"))}</p>
                <p className="mt-2 text-xs text-gold font-bold uppercase tracking-wider">
                    {tr(data?.newsletter?.social_proof, t("newsletter.social_proof"))}
                </p>
                <form
                    onSubmit={submit}
                    className="mt-8 flex items-stretch max-w-md mx-auto rounded-md overflow-hidden border border-white/15 bg-white/5"
                    data-testid="home-newsletter-form"
                >
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t("newsletter.placeholder")}
                        className="flex-1 bg-transparent text-white placeholder:text-white/50 px-4 py-3 text-sm outline-none"
                        data-testid="home-newsletter-email"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-gold hover:bg-gold-700 text-dark-navy font-bold px-5 inline-flex items-center gap-2"
                        data-testid="home-newsletter-submit"
                    >
                        <Send className="w-4 h-4" />
                        {t("actions.subscribe")}
                    </button>
                </form>
            </div>
        </section>
    );
}

function SectionHeader({ eyebrow, title, subtitle, link }) {
    return (
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
                <span className="text-xs uppercase tracking-[0.2em] text-teal-700 font-semibold">{eyebrow}</span>
                <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-dark-navy tracking-tight">{title}</h2>
                {subtitle && <p className="mt-2 text-deep-blue/80 max-w-2xl">{subtitle}</p>}
            </div>
            {link && (
                <Link
                    to={link.href}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:text-teal whitespace-nowrap"
                >
                    {link.label} <ArrowRight className="w-4 h-4" />
                </Link>
            )}
        </div>
    );
}

function LiveRow({ item, lang }) {
    const TrendIcon = item.trend_direction === "up" ? TrendingUp : item.trend_direction === "down" ? TrendingDown : Minus;
    const tColor = item.trend_direction === "up" ? "text-data-green" : item.trend_direction === "down" ? "text-data-red" : "text-white/50";
    const name = (item.name && (item.name[lang] || item.name.tr)) || "—";
    return (
        <div className="flex items-center justify-between border-b border-white/5 last:border-0 py-2.5">
            <span className="text-sm text-white/80 truncate pr-3">{name}</span>
            <div className="flex items-center gap-3 shrink-0">
                <span className="font-data text-sm font-semibold text-white tabular-nums">
                    {item.value}{item.unit}
                </span>
                <span className={`inline-flex items-center gap-0.5 text-xs font-data font-semibold ${tColor}`}>
                    <TrendIcon className="w-3 h-3" /> {item.trend_percentage}%
                </span>
            </div>
        </div>
    );
}

const PULSE_FALLBACK_SOURCES = ["2024 · BAKANLIK", "TURİZM İŞLETME + YATIRIM", "TÜYAP RAPORU", "2024 TOPLAMI"];

const formatPulseNumber = (v) => {
    if (v == null || isNaN(v)) return "—";
    if (Math.abs(v) >= 1000) return Math.round(v).toLocaleString("tr-TR");
    if (v % 1 !== 0) return v.toFixed(1).replace(".", ",");
    return v.toLocaleString("tr-TR");
};

const compactUnit = (u) => {
    if (!u) return "";
    const c = String(u).trim().toLowerCase();
    if (c === "%") return "%";
    if (c.includes("milyar")) return c.includes("$") || c.includes("usd") ? "Mr $" : "Mr";
    if (c.includes("milyon")) return "M";
    if (c.includes("bin")) return "B";
    if (c === "adet") return "";
    return u;
};

function PulseBarChart({ bars = 14 }) {
    const heights = useMemo(() => {
        const arr = Array.from({ length: bars }, (_, i) => {
            const t = i / (bars - 1);
            return 28 + t * 55 + Math.sin(t * Math.PI * 2.4) * 7;
        });
        arr[bars - 2] = 95;
        return arr;
    }, [bars]);
    const maxIdx = heights.indexOf(Math.max(...heights));
    return (
        <div className="flex items-end justify-between gap-1.5 h-24" aria-hidden>
            {heights.map((h, i) => (
                <div
                    key={i}
                    className="flex-1 rounded-md transition-all"
                    style={{
                        height: `${h}%`,
                        background:
                            "linear-gradient(180deg, rgba(57,200,193,1) 0%, rgba(57,200,193,0.55) 50%, rgba(57,200,193,0.18) 100%)",
                        boxShadow: i === maxIdx ? "0 0 18px 2px rgba(57,200,193,0.55)" : "none",
                        animation: `pulseBarFade 3.2s ease-in-out ${i * 0.07}s infinite`,
                    }}
                />
            ))}
        </div>
    );
}

function PulseRow({ item, idx, lang }) {
    const name = (item.name && (item.name[lang] || item.name.tr)) || "—";
    const source = item.source || PULSE_FALLBACK_SOURCES[idx] || "";
    const isUp = item.trend_direction !== "down";
    const TrendIcon = isUp ? TrendingUp : TrendingDown;
    const valueText = formatPulseNumber(item.value);
    const unitText = compactUnit(item.unit);
    const trendValue = formatPulseNumber(item.trend_percentage);
    return (
        <div
            data-testid={`pulse-row-${idx}`}
            className="grid grid-cols-[1fr_auto] items-center gap-4 py-3.5 border-b border-white/5 last:border-0"
        >
            <div className="min-w-0">
                <div className="text-sm text-white/95 font-semibold truncate">{name}</div>
                <div className="mt-0.5 text-[10px] uppercase tracking-[0.16em] text-white/40 font-semibold truncate">
                    {source}
                </div>
            </div>
            <div className="flex items-center gap-2.5 shrink-0">
                <div className="font-data text-2xl font-bold text-white tabular-nums leading-none">
                    {valueText}
                    {unitText && (
                        <span className="ml-0.5 text-xs font-semibold text-white/55">{unitText}</span>
                    )}
                </div>
                <span
                    className={`inline-flex items-center gap-0.5 text-[11px] font-bold tabular-nums px-2 py-1 rounded-full border ${
                        isUp
                            ? "text-data-green border-data-green/40 bg-data-green/10"
                            : "text-data-red border-data-red/40 bg-data-red/10"
                    }`}
                >
                    <TrendIcon className="w-3 h-3" /> {trendValue}
                </span>
            </div>
        </div>
    );
}

function DataShowcaseCard({ item, lang }) {
    const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
    const Icon = CATEGORY_ICONS[item.category] || BarChart3;
    const TrendIcon = item.trend_direction === "up" ? TrendingUp : item.trend_direction === "down" ? TrendingDown : Minus;
    const tColor = item.trend_direction === "up" ? "text-data-green" : item.trend_direction === "down" ? "text-data-red" : "text-deep-blue/60";
    const name = (item.name && (item.name[lang] || item.name.tr)) || "";
    const series = useMemo(() => {
        const dir = item.trend_direction === "down" ? -1 : item.trend_direction === "up" ? 1 : 0;
        const base = item.value || 1;
        return Array.from({ length: 10 }, (_, i) => base * (1 + dir * 0.05 * Math.sin((i / 9) * Math.PI) * (1 + i / 30)));
    }, [item]);

    return (
        <div ref={ref} className="lift-card bg-white rounded-lg border border-slate-200 hover:border-teal/40 shadow-md hover:shadow-xl p-5">
            <div className="flex items-start justify-between">
                <span className="inline-flex w-9 h-9 rounded-md bg-teal/10 text-teal-700 items-center justify-center">
                    <Icon className="w-4 h-4" />
                </span>
                <span className={`inline-flex items-center gap-1 text-xs font-data font-semibold ${tColor}`}>
                    <TrendIcon className="w-3 h-3" />
                    {item.trend_percentage}%
                </span>
            </div>
            <div className="mt-3 text-xs uppercase tracking-wider text-deep-blue/70 font-semibold truncate">{name}</div>
            <div className="mt-1 font-data text-3xl font-bold text-dark-navy">
                {inView ? <CountUp end={item.value} duration={1.6} decimals={item.value % 1 === 0 ? 0 : 1} separator="." decimal="," /> : "0"}
                <span className="ml-1 text-sm text-deep-blue/70 font-semibold">{item.unit}</span>
            </div>
            <div className="mt-2 h-7">
                <Sparkline data={series} color={item.trend_direction === "down" ? "#ef4444" : "#39c8c1"} />
            </div>
        </div>
    );
}
